/**
 * Checks whether an unknown error came back from an HTTP layer with the
 * given status. Tolerant of axios-shaped (`error.response.status`) and
 * fetch-shaped (`error.status`) errors so the helper works across the
 * BFF clients we use today and whatever lands tomorrow.
 * @param {unknown} error - The error to inspect.
 * @param {number} status - The HTTP status to match.
 * @returns {boolean} True when `error` carries the given status.
 */
export function isHttpError(error: unknown, status: number): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as {
    response?: { status?: number };
    status?: number | string;
  };
  if (e.response?.status === status) return true;
  return Number.parseInt(String(e.status), 10) === status;
}

/**
 * Extracts a human-readable message from an unknown error. Useful when
 * surfacing `error` from a TanStack Query hook in UI without having to
 * narrow the type at every call site.
 * @param {unknown} error - The error to read.
 * @returns {string} A best-effort message.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Unknown error";
}
